import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  MembershipSchema,
  MembershipsListResponseSchema,
  type Membership,
  type UpdateMembershipRoleRequest,
} from '@repo/api-client/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { deleteJSON, getJSON, patchJSON, postJSON } from '../internal/http';

const OkSchema = z.object({ ok: z.boolean() }).passthrough();

/** List members of an org. Read-only for owners + admins. */
export function useMemberships(overrideOrgId?: string) {
  const http = useHttpClient();
  const activeOrgId = useActiveOrganizationId();
  const orgId = overrideOrgId ?? activeOrgId;

  const {
    error,
    isPending: loading,
    data,
    refetch,
  } = useQuery({
    queryKey: orgId ? queryKeys.memberships.list(orgId) : ['memberships', 'idle'],
    queryFn: () =>
      getJSON(http, `/orgs/${orgId}/members`, MembershipsListResponseSchema),
    enabled: !!orgId,
  });

  return {
    memberships: data as Membership[] | undefined,
    loading,
    error,
    refetch,
  };
}

/**
 * Promote/demote a member between `admin` and `member`. Owner role
 * transitions go through `useTransferOwnership` instead so the rules
 * stay explicit.
 */
export function useUpdateMembershipRole() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      orgId,
      userId,
      role,
    }: {
      orgId: string;
      userId: string;
      role: UpdateMembershipRoleRequest['role'];
    }) =>
      patchJSON(
        http,
        `/orgs/${orgId}/members/${userId}`,
        { role },
        MembershipSchema,
      ),
    onSuccess: (_membership, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.memberships.list(orgId) });
    },
  });

  return {
    updateRole: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/** Remove a member from the org (or self-leave). */
export function useRemoveMember() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      deleteJSON(http, `/orgs/${orgId}/members/${userId}`, OkSchema),
    onSuccess: (_data, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.memberships.list(orgId) });
    },
  });

  return {
    removeMember: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/** Transfer the `owner` role to another member; demotes the previous owner to `admin`. */
export function useTransferOwnership() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      postJSON(
        http,
        `/orgs/${orgId}/members/${userId}/transfer-ownership`,
        {},
        OkSchema,
      ),
    onSuccess: (_data, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.memberships.list(orgId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(orgId) });
    },
  });

  return {
    transferOwnership: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
